import React from "react";
import { DateTime } from "luxon";

import {
  CalendarEvent,
  CalendarTask,
  CalendarUnavailability,
} from "../../types";
import { fromDayMarker, parseBackendIso } from "../../utils";
import { Props } from "./types";
import { Filter } from "@common/filter";
import { Event } from "./event";
import { Task } from "./task";
import { Unavailability } from "./unavailability";
import { DayCreationMenu } from "../../week/dayCreationMenu";
import styles from "./styles.scss";

const TIME_HEIGHT = 35;

function doTimesOverlap(
  start1: DateTime,
  end1: DateTime,
  start2: DateTime,
  end2: DateTime,
): boolean {
  return start1 < end2 && start2 < end1;
}

function getDayBounds(
  day: Date,
  userTimezone: string,
): { dayStart: DateTime; dayEnd: DateTime } {
  const dayDt = fromDayMarker(day);
  const dayStart = DateTime.fromObject(
    { year: dayDt.year, month: dayDt.month, day: dayDt.day },
    { zone: userTimezone },
  ).startOf("day");
  const dayEnd = dayStart.plus({ days: 1 });
  return { dayStart, dayEnd };
}

function clipToDay(
  start: DateTime,
  end: DateTime,
  dayStart: DateTime,
  dayEnd: DateTime,
) {
  return {
    displayStart: start < dayStart ? dayStart : start,
    displayEnd: end > dayEnd ? dayEnd : end,
  };
}

function isItemOnDay(
  start: DateTime,
  end: DateTime,
  dayStart: DateTime,
  dayEnd: DateTime,
): boolean {
  return start < dayEnd && end > dayStart;
}

function hoursSinceDayStart(date: DateTime, dayStart: DateTime): number {
  return (date.toMillis() - dayStart.toMillis()) / (60 * 60 * 1000);
}

function getTimeInHours(date: DateTime): number {
  return date.hour + date.minute / 60;
}

type TimeItem = {
  uuid: string;
  startTime: DateTime;
  endTime: DateTime;
  type: "event" | "task" | "unavailability";
  data: CalendarEvent | CalendarTask | CalendarUnavailability;
};

function computeLayout(
  items: TimeItem[],
): Map<string, { lane: number; totalLanes: number }> {
  const layout = new Map<string, { lane: number; totalLanes: number }>();
  const visited = new Set<string>();
  const clusters: TimeItem[][] = [];

  for (const item of items) {
    if (visited.has(item.uuid)) continue;
    const cluster: TimeItem[] = [];
    const queue = [item];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current.uuid)) continue;
      visited.add(current.uuid);
      cluster.push(current);
      for (const other of items) {
        if (
          !visited.has(other.uuid) &&
          doTimesOverlap(
            current.startTime,
            current.endTime,
            other.startTime,
            other.endTime,
          )
        ) {
          queue.push(other);
        }
      }
    }
    clusters.push(cluster);
  }

  for (const cluster of clusters) {
    const sorted = [...cluster].sort(
      (a, b) => a.startTime.toMillis() - b.startTime.toMillis(),
    );
    const laneEndTimes: DateTime[] = [];

    for (const item of sorted) {
      let assignedLane = -1;
      for (let i = 0; i < laneEndTimes.length; i++) {
        if (laneEndTimes[i] <= item.startTime) {
          assignedLane = i;
          laneEndTimes[i] = item.endTime;
          break;
        }
      }
      if (assignedLane === -1) {
        assignedLane = laneEndTimes.length;
        laneEndTimes.push(item.endTime);
      }
      layout.set(item.uuid, { lane: assignedLane, totalLanes: 0 });
    }

    const totalLanes = laneEndTimes.length;
    for (const item of cluster) {
      layout.set(item.uuid, { lane: layout.get(item.uuid)!.lane, totalLanes });
    }
  }

  return layout;
}

const PREVIEW_UUID = "__preview__";

function getItemsForDay(
  day: Date,
  events: CalendarEvent[],
  tasks: CalendarTask[],
  unavailabilities: CalendarUnavailability[],
  userTimezone: string,
): TimeItem[] {
  const { dayStart, dayEnd } = getDayBounds(day, userTimezone);

  const dayEvents = events.filter((e) =>
    isItemOnDay(
      parseBackendIso(e.startTime).setZone(userTimezone),
      parseBackendIso(e.endTime).setZone(userTimezone),
      dayStart,
      dayEnd,
    ),
  );
  const dayTasks = tasks.filter(
    (t) =>
      t.startDate &&
      t.endDate &&
      isItemOnDay(
        parseBackendIso(t.startDate).setZone(userTimezone),
        parseBackendIso(t.endDate).setZone(userTimezone),
        dayStart,
        dayEnd,
      ),
  );
  const dayUnavailabilities = unavailabilities.filter(
    (u) =>
      u.partialUnavailability &&
      isItemOnDay(
        parseBackendIso(u.startDate).setZone(userTimezone),
        parseBackendIso(u.endDate || u.startDate).setZone(userTimezone),
        dayStart,
        dayEnd,
      ),
  );

  return [
    ...dayUnavailabilities.map((u) => {
      const { displayStart, displayEnd } = clipToDay(
        parseBackendIso(u.startDate).setZone(userTimezone),
        parseBackendIso(u.endDate || u.startDate).setZone(userTimezone),
        dayStart,
        dayEnd,
      );
      return {
        uuid: u.uuid,
        startTime: displayStart,
        endTime: displayEnd,
        type: "unavailability" as const,
        data: u,
      };
    }),
    ...dayEvents.map((e) => {
      const { displayStart, displayEnd } = clipToDay(
        parseBackendIso(e.startTime).setZone(userTimezone),
        parseBackendIso(e.endTime).setZone(userTimezone),
        dayStart,
        dayEnd,
      );
      return {
        uuid: e.uuid,
        startTime: displayStart,
        endTime: displayEnd,
        type: "event" as const,
        data: e,
      };
    }),
    ...dayTasks.map((t) => {
      const { displayStart, displayEnd } = clipToDay(
        parseBackendIso(t.startDate!).setZone(userTimezone),
        parseBackendIso(t.endDate!).setZone(userTimezone),
        dayStart,
        dayEnd,
      );
      return {
        uuid: t.uuid,
        startTime: displayStart,
        endTime: displayEnd,
        type: "task" as const,
        data: t,
      };
    }),
  ];
}

function TimeView({ week, events, tasks, unavailabilities, filter }: Props) {
  const userTimezone = DateTime.local().zoneName;
  const [currentTime, setCurrentTime] = React.useState<DateTime>(() =>
    DateTime.now().setZone(userTimezone),
  );
  const containerRef = React.useRef<HTMLDivElement>(null);

  const [creationMenu, setCreationMenu] = React.useState<{
    day: Date;
    dayIndex: number;
    x: number;
    y: number;
    openedLeft: boolean;
  } | null>(null);

  const [hoverSlot, setHoverSlot] = React.useState<{
    dayIndex: number;
    top: number;
  } | null>(null);

  function getPreviewLayout(
    dayIndex: number,
    slotStart: DateTime,
    slotEnd: DateTime,
  ): { leftPct: number; widthPct: number; left: string; width: string } {
    const columnWidth = 100 / week.length;
    const columnLeft = columnWidth * dayIndex;
    const day = week[dayIndex];
    const dayItems = getItemsForDay(
      day,
      events,
      tasks,
      unavailabilities,
      userTimezone,
    );
    const previewItem: TimeItem = {
      uuid: PREVIEW_UUID,
      startTime: slotStart,
      endTime: slotEnd,
      type: "event",
      data: {} as CalendarEvent,
    };
    const layout = computeLayout([...dayItems, previewItem]);
    const { lane, totalLanes } = layout.get(PREVIEW_UUID) ?? {
      lane: 0,
      totalLanes: 1,
    };
    const slotWidth = (columnWidth * 0.95) / totalLanes;
    const leftPct = columnLeft + slotWidth * lane;
    const widthPct = slotWidth;
    return {
      leftPct,
      widthPct,
      left: `${leftPct}%`,
      width: `${widthPct}%`,
    };
  }

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(DateTime.now().setZone(userTimezone));
    }, 60000);

    return () => clearInterval(interval);
  }, [userTimezone]);

  const currentHour = currentTime.hour + currentTime.minute / 60;
  const currentLineTop = currentHour * TIME_HEIGHT;

  const isTodayInWeek = week.some((day) => {
    const dayDt = fromDayMarker(day);
    return (
      dayDt.year === currentTime.year &&
      dayDt.month === currentTime.month &&
      dayDt.day === currentTime.day
    );
  });

  function getSlotFromMouseEvent(e: React.MouseEvent<HTMLDivElement>) {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return null;

    const relativeX = e.clientX - containerRect.left;
    const relativeY = e.clientY - containerRect.top;

    const dayWidth = containerRect.width / week.length;
    const dayIndex = Math.max(
      0,
      Math.min(week.length - 1, Math.floor(relativeX / dayWidth)),
    );

    const hour = Math.max(0, Math.min(23, Math.floor(relativeY / TIME_HEIGHT)));
    const isHalfHour = relativeY % TIME_HEIGHT >= TIME_HEIGHT / 2;
    const top = hour * TIME_HEIGHT + (isHalfHour ? TIME_HEIGHT / 2 : 0);

    return { dayIndex, top };
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const slot = getSlotFromMouseEvent(e);
    setHoverSlot(slot);
  }

  function handleMouseLeave() {
    setHoverSlot(null);
  }

  function handleDoubleClick(e: React.MouseEvent<HTMLDivElement>) {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    const relativeX = e.clientX - containerRect.left;
    const relativeY = e.clientY - containerRect.top;

    const dayWidth = containerRect.width / week.length;
    const dayIndex = Math.max(
      0,
      Math.min(week.length - 1, Math.floor(relativeX / dayWidth)),
    );

    const day = week[dayIndex];
    const hour = Math.max(0, Math.min(23, Math.floor(relativeY / TIME_HEIGHT)));
    const minute = relativeY % TIME_HEIGHT >= TIME_HEIGHT / 2 ? 30 : 0;

    const dayDt = fromDayMarker(day);
    const dayWithTime = DateTime.fromObject(
      {
        year: dayDt.year,
        month: dayDt.month,
        day: dayDt.day,
        hour,
        minute,
        second: 0,
      },
      { zone: userTimezone },
    );

    const previewCenterY =
      containerRect.top +
      getTimeInHours(dayWithTime) * TIME_HEIGHT +
      TIME_HEIGHT / 2;

    const slotEnd = dayWithTime.plus({ hours: 1 });
    const { leftPct, widthPct } = getPreviewLayout(
      dayIndex,
      dayWithTime,
      slotEnd,
    );
    const previewLeftPx =
      containerRect.left + (leftPct / 100) * containerRect.width;
    const previewRightPx =
      previewLeftPx + (widthPct / 100) * containerRect.width;
    const openedLeft = previewRightPx + 380 > window.innerWidth;
    const borderX = openedLeft ? previewLeftPx : previewRightPx;

    setCreationMenu({
      day: dayWithTime.toJSDate(),
      dayIndex,
      x: borderX,
      y: previewCenterY,
      openedLeft,
    });
  }

  return (
    <div
      ref={containerRef}
      className={styles.timeView}
      onDoubleClick={handleDoubleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {new Array(24).fill(0).map((_, index) => (
        <div
          key={index}
          className={styles.time}
          style={{ height: TIME_HEIGHT }}
        >
          <p className={styles.hours}>{index < 9 ? `0${index}` : index}:00</p>
        </div>
      ))}

      {week.map((day, dayIndex) => {
        const dayDt = fromDayMarker(day);
        const isWeekend = dayDt.weekday === 6 || dayDt.weekday === 7;
        return (
          <div
            key={`col-${dayIndex}`}
            className={styles.dayColumn}
            style={{
              width: `${100 / week.length}%`,
              left: `${(100 / week.length) * dayIndex}%`,
              backgroundColor: isWeekend
                ? "rgba(0, 0, 0, 0.02)"
                : "transparent",
            }}
          />
        );
      })}

      {isTodayInWeek && (
        <div
          className={styles.currentTimeLine}
          style={{ top: `${currentLineTop}px` }}
        >
          <div className={styles.currentTimeDot} />
        </div>
      )}

      <Filter
        data={events}
        filter={filter}
        extractCriteria={({ project, ...event }) => ({ event, project })}
      >
        {(filteredEvents) => (
          <Filter
            data={tasks}
            filter={filter}
            extractCriteria={({ project, ...task }) => ({ task, project })}
          >
            {(filteredTasks) => (
              <React.Fragment>
                {week.map((day, dayIndex) => {
                  const { dayStart, dayEnd } = getDayBounds(day, userTimezone);

                  const dayEvents = filteredEvents.filter((event) =>
                    isItemOnDay(
                      parseBackendIso(event.startTime).setZone(userTimezone),
                      parseBackendIso(event.endTime).setZone(userTimezone),
                      dayStart,
                      dayEnd,
                    ),
                  );

                  const dayTasks = filteredTasks.filter(
                    (task) =>
                      !!task.startDate &&
                      !!task.endDate &&
                      isItemOnDay(
                        parseBackendIso(task.startDate).setZone(userTimezone),
                        parseBackendIso(task.endDate).setZone(userTimezone),
                        dayStart,
                        dayEnd,
                      ),
                  );

                  const dayUnavailabilities = unavailabilities.filter(
                    (u) =>
                      !!u.partialUnavailability &&
                      isItemOnDay(
                        parseBackendIso(u.startDate).setZone(userTimezone),
                        parseBackendIso(u.endDate || u.startDate).setZone(
                          userTimezone,
                        ),
                        dayStart,
                        dayEnd,
                      ),
                  );

                  const allItems: TimeItem[] = [
                    ...dayUnavailabilities.map((u) => {
                      const start = parseBackendIso(u.startDate).setZone(
                        userTimezone,
                      );
                      const end = parseBackendIso(
                        u.endDate || u.startDate,
                      ).setZone(userTimezone);
                      return {
                        uuid: u.uuid,
                        startTime: start < dayStart ? dayStart : start,
                        endTime: end > dayEnd ? dayEnd : end,
                        type: "unavailability" as const,
                        data: u,
                      };
                    }),
                    ...dayEvents.map((e) => {
                      const start = parseBackendIso(e.startTime).setZone(
                        userTimezone,
                      );
                      const end = parseBackendIso(e.endTime).setZone(
                        userTimezone,
                      );
                      return {
                        uuid: e.uuid,
                        startTime: start < dayStart ? dayStart : start,
                        endTime: end > dayEnd ? dayEnd : end,
                        type: "event" as const,
                        data: e,
                      };
                    }),
                    ...dayTasks.map((t) => {
                      const start = parseBackendIso(t.startDate!).setZone(
                        userTimezone,
                      );
                      const end = parseBackendIso(t.endDate!).setZone(
                        userTimezone,
                      );
                      return {
                        uuid: t.uuid,
                        startTime: start < dayStart ? dayStart : start,
                        endTime: end > dayEnd ? dayEnd : end,
                        type: "task" as const,
                        data: t,
                      };
                    }),
                  ];

                  const columnWidth = 100 / week.length;
                  const columnLeft = columnWidth * dayIndex;
                  const layout = computeLayout(allItems);

                  return (
                    <React.Fragment key={dayIndex}>
                      {allItems.map((item) => {
                        const startHour = hoursSinceDayStart(
                          item.startTime,
                          dayStart,
                        );
                        const endHour = hoursSinceDayStart(
                          item.endTime,
                          dayStart,
                        );
                        const { lane, totalLanes } = layout.get(item.uuid) ?? {
                          lane: 0,
                          totalLanes: 1,
                        };
                        const slotWidth = (columnWidth * 0.95) / totalLanes;

                        return (
                          <div
                            key={`${item.uuid}-${dayIndex}`}
                            className={styles.event}
                            style={{
                              height: TIME_HEIGHT * (endHour - startHour),
                              top: startHour * TIME_HEIGHT,
                              left: `calc(${columnLeft}% + ${slotWidth * lane}%)`,
                              width: `${slotWidth}%`,
                            }}
                          >
                            {item.type === "event" ? (
                              <Event event={item.data as CalendarEvent} />
                            ) : item.type === "task" ? (
                              <Task task={item.data as CalendarTask} />
                            ) : (
                              <Unavailability
                                unavailability={
                                  item.data as CalendarUnavailability
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            )}
          </Filter>
        )}
      </Filter>
      {hoverSlot &&
        !creationMenu &&
        (() => {
          const slotHour = Math.floor(hoverSlot.top / TIME_HEIGHT);
          const slotMinute =
            hoverSlot.top % TIME_HEIGHT >= TIME_HEIGHT / 2 ? 30 : 0;
          const day = week[hoverSlot.dayIndex];
          const dayDt = fromDayMarker(day);
          const slotStart = DateTime.fromObject(
            {
              year: dayDt.year,
              month: dayDt.month,
              day: dayDt.day,
              hour: slotHour,
              minute: slotMinute,
              second: 0,
            },
            { zone: userTimezone },
          );
          const slotEnd = slotStart.plus({ minutes: 30 });
          const { left, width } = getPreviewLayout(
            hoverSlot.dayIndex,
            slotStart,
            slotEnd,
          );
          return (
            <div
              className={styles.hoverSlot}
              style={{
                top: hoverSlot.top,
                height: TIME_HEIGHT / 2,
                left,
                width,
              }}
            />
          );
        })()}

      {creationMenu &&
        (() => {
          const openedLeft = creationMenu.openedLeft;
          const dayDt = DateTime.fromJSDate(creationMenu.day).setZone(
            userTimezone,
          );
          const slotEnd = dayDt.plus({ hours: 1 });
          const { left, width } = getPreviewLayout(
            creationMenu.dayIndex,
            dayDt,
            slotEnd,
          );
          return (
            <div
              className={`${styles.creationPreview} ${openedLeft ? styles.creationPreviewConnectLeft : styles.creationPreviewConnectRight}`}
              style={{
                top: getTimeInHours(dayDt) * TIME_HEIGHT,
                height: TIME_HEIGHT,
                left,
                width,
              }}
            >
              <span className={styles.creationPreviewTime}>
                {dayDt.toFormat("HH:mm")}
              </span>
            </div>
          );
        })()}

      {creationMenu && (
        <DayCreationMenu
          day={creationMenu.day}
          x={creationMenu.x}
          y={creationMenu.y}
          openedLeft={creationMenu.openedLeft}
          onClose={() => setCreationMenu(null)}
        />
      )}
    </div>
  );
}

export { TimeView };
